/**
 * BulkImportPage — two-phase CSV/XLSX import (P1 Tier-A, marketplace.bulk_import).
 * Upload → dry-run preview (summary counts + inline row-error table + a link to
 * the full errors CSV) → commit → poll job status → cancel. Phase drives the UI:
 * the upload mutation returns a job_id; useImportJob polls until dry_run_completed
 * (commit gate) or completed/failed (terminal). Loading/empty/error states are
 * drafted consistent-with-system (flagged in PR — no prototype for admin).
 */
import { useMemo, useRef, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Button, GPill } from "@garden/ui";
import { ADMIN_NS, registerAdminI18n } from "@/features/admin/api";
import { AdminTable } from "@/features/admin/components/AdminTable";
import {
  IMPORT_JOB_KINDS,
  type DryRunInlineError,
  type ImportJobKind,
} from "@/features/admin/import/api";
import {
  useCancelImport,
  useCommitImport,
  useImportErrorsLink,
  useImportJob,
  useUploadImport,
} from "@/features/admin/import/hooks";

const SELECT_CLASS =
  "min-h-[44px] w-full max-w-sm rounded-[var(--g-radius-sm)] border border-[var(--g-hairline)] bg-[var(--g-surface)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--g-primary)]";

export function BulkImportPage(): ReactElement {
  registerAdminI18n();
  const { t } = useTranslation(ADMIN_NS);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const [kind, setKind] = useState<ImportJobKind>("sellers_and_products");
  const [file, setFile] = useState<File | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);

  const upload = useUploadImport();
  const commit = useCommitImport();
  const cancel = useCancelImport();
  const { data: job } = useImportJob(jobId);
  const phase = job?.phase;
  const { data: errorsLink } = useImportErrorsLink(jobId, phase === "dry_run_completed");

  const handleUpload = (): void => {
    if (!file) return;
    upload.mutate(
      { kind, file },
      {
        onSuccess: (res) => setJobId(res.job_id),
        onError: (err) => toast.error(t(`import.errorKind.${err.message}`, { defaultValue: t("error.generic") })),
      },
    );
  };
  const handleCommit = (): void => {
    if (!jobId) return;
    commit.mutate(jobId, {
      onSuccess: () => toast.success(t("import.committedToast")),
      onError: () => toast.error(t("error.generic")),
    });
  };
  const handleCancel = (): void => {
    if (!jobId) return;
    cancel.mutate(
      { id: jobId, reason: "cancelled by administrator" },
      { onSuccess: () => toast.success(t("import.cancelledToast")), onError: () => toast.error(t("error.generic")) },
    );
  };
  const reset = (): void => { setJobId(null); setFile(null); if (fileRef.current) fileRef.current.value = ""; };

  const errorRows: DryRunInlineError[] = useMemo(
    () => job?.dry_run_summary?.inline_errors ?? [],
    [job],
  );
  const errorColumns = [
    { key: "row", label: t("import.errorsTable.row"), align: "right" as const },
    { key: "field", label: t("import.errorsTable.field") },
    { key: "kind", label: t("import.errorsTable.kind") },
  ];

  const isActive = phase === "queued" || phase === "dry_run_running" || phase === "committing";
  const summary = job?.dry_run_summary;
  const commitResult = job?.commit_summary;

  return (
    <div data-testid="admin-import-page" className="flex flex-col gap-5">
      <h1 className="text-xl font-bold">{t("import.title")}</h1>

      {/* Upload controls */}
      <section className="flex flex-col gap-3">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="import-kind" className="text-[13px] font-semibold text-foreground">{t("import.kind")}</label>
          <select id="import-kind" data-testid="import-kind-select" className={SELECT_CLASS} value={kind}
            onChange={(e) => setKind(e.target.value as ImportJobKind)}>
            {IMPORT_JOB_KINDS.filter((k) => k !== "products").map((k) => (
              <option key={k} value={k}>
                {t(k === "sellers" ? "import.kindSellers" : "import.kindSellersAndProducts")}
              </option>
            ))}
          </select>
        </div>
        <input
          ref={fileRef}
          type="file"
          accept=".csv,.xlsx,.xls"
          data-testid="import-file-input"
          aria-label={t("import.chooseFile")}
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          className="text-sm"
        />
        <Button variant="primary" size="md" data-testid="import-upload-btn" disabled={!file} loading={upload.isPending}
          onClick={handleUpload} className="min-h-[44px] w-fit">
          {t("import.upload")}
        </Button>
        {/* PIM Wave P5 Task 8 — documents the `attr:<field_slug>` dynamic
            attribute columns the commit worker merges (Task 5/6); zero
            behavior change to the upload/dry-run/commit flow above. */}
        <p data-testid="import-attr-columns-help" className="text-sm text-[var(--g-secondary)]">
          {t("import.attrColumnsHelp")}
        </p>
      </section>

      {jobId === null ? (
        <p data-testid="admin-import-empty" className="py-6 text-sm text-[var(--g-secondary)]">{t("import.empty")}</p>
      ) : (
        <section className="flex flex-col gap-4">
          <div className="flex items-center gap-2">
            <GPill tone={phase === "failed" ? "error" : phase === "completed" ? "success" : "neutral"} size="sm">
              {t(`import.phase.${phase ?? "queued"}`)}
            </GPill>
            {isActive ? <span className="text-sm text-[var(--g-secondary)]">{t("import.checking")}</span> : null}
          </div>

          {summary ? (
            <div data-testid="import-summary" className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <SummaryStat label={t("import.summary.total")} value={summary.total_rows} />
              <SummaryStat label={t("import.summary.valid")} value={summary.valid_count} />
              <SummaryStat label={t("import.summary.duplicates")} value={summary.duplicate_count} />
              <SummaryStat label={t("import.summary.errors")} value={summary.error_count} />
            </div>
          ) : null}

          {errorRows.length > 0 ? (
            <section className="flex flex-col gap-2">
              <h2 className="text-sm font-semibold">{t("import.errorsTable.title")}</h2>
              <AdminTable<DryRunInlineError>
                columns={errorColumns}
                rows={errorRows}
                rowKey={(r) => `${r.row_index}-${r.field}-${r.kind}`}
                renderCell={(r, key) =>
                  key === "row" ? String(r.row_index) : key === "field" ? r.field : t(`import.errorKind.${r.kind}`, { defaultValue: r.kind })
                }
                renderMobileCard={(r) => (
                  <div className="flex flex-col gap-0.5">
                    <span className="font-medium">#{r.row_index} · {r.field}</span>
                    <span className="text-sm text-[var(--g-error)]">{t(`import.errorKind.${r.kind}`, { defaultValue: r.kind })}</span>
                  </div>
                )}
                rowAriaLabel={(r) => `${r.row_index} ${r.field}`}
                testid="import-errors"
                isLoading={false}
                isError={false}
                emptyLabel=""
                errorLabel=""
              />
              {errorsLink?.url ? (
                <a href={errorsLink.url} data-testid="import-errors-csv" className="text-sm font-medium text-[var(--g-primary)] underline">
                  {t("import.errorsTable.downloadCsv")}
                </a>
              ) : null}
            </section>
          ) : null}

          {commitResult ? (
            <div data-testid="import-result" className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <SummaryStat label={t("import.result.createdSellers")} value={commitResult.created_sellers} />
              <SummaryStat label={t("import.result.updatedSellers")} value={commitResult.updated_sellers} />
              <SummaryStat label={t("import.result.createdProducts")} value={commitResult.created_products} />
              <SummaryStat label={t("import.result.updatedProducts")} value={commitResult.updated_products} />
            </div>
          ) : null}

          <div className="flex flex-wrap gap-2">
            {phase === "dry_run_completed" ? (
              <Button variant="primary" size="md" data-testid="import-commit-btn" loading={commit.isPending} onClick={handleCommit} className="min-h-[44px]">
                {t("import.commit")}
              </Button>
            ) : null}
            {phase !== "completed" && phase !== "failed" ? (
              <Button variant="destructive" size="md" data-testid="import-cancel-btn" loading={cancel.isPending} onClick={handleCancel} className="min-h-[44px]">
                {t("import.cancel")}
              </Button>
            ) : null}
            {phase === "completed" || phase === "failed" ? (
              <Button variant="secondary" size="md" data-testid="import-new-btn" onClick={reset} className="min-h-[44px]">
                {t("import.newUpload")}
              </Button>
            ) : null}
          </div>
        </section>
      )}
    </div>
  );
}

function SummaryStat({ label, value }: { label: string; value: number }): ReactElement {
  return (
    <div className="rounded-[var(--g-radius-sm)] border border-[var(--g-hairline)] bg-[var(--g-surface)] p-3">
      <div className="text-2xl font-bold tabular-nums">{value}</div>
      <div className="text-xs text-[var(--g-secondary)]">{label}</div>
    </div>
  );
}

export default BulkImportPage;
